import type { ApiProduct, ApiSku } from "@/lib/api";
import { savings, type Savings } from "@/lib/pdp-pricing";
import { firstAdditionalStill, primaryImage } from "@/lib/product-images";

/**
 * Paket Hemat: the storefront's name for a package product, priced below what
 * its contents cost bought one by one.
 *
 * Kept free of React so the rules are tested in one place: which packages are
 * shown, in what order, and what the header and the page headline claim.
 */
export interface PaketHemat {
  product: ApiProduct;
  sku: ApiSku;
  savings: Savings;
  image?: string;
  soldOut: boolean;
}

/**
 * Every package worth advertising, buyable ones first, biggest saving first.
 *
 * A package the server has flagged invalid (its contents now cost less than
 * it, or one of them was switched off) has no honest saving, so `savings`
 * drops it rather than this page claiming one. So does a catalogue that came
 * back undecorated: without a compare-at there is nothing to say.
 */
export function paketHematList(products: ApiProduct[]): PaketHemat[] {
  const out: PaketHemat[] = [];
  for (const product of products) {
    if (product.line !== "package") continue;
    const sku = product.skus?.find((s) => s.isPackage && s.isActive);
    if (!sku) continue;
    const save = savings(sku);
    if (!save) continue;
    out.push({
      product,
      sku,
      savings: save,
      // A package with no colour photograph uses its own Additional photo, and
      // only then borrows its first item's, as the catalogue card does.
      image:
        primaryImage(product)?.publicUrl ??
        firstAdditionalStill(product)?.publicUrl ??
        borrowed(sku.packageItems?.find((i) => i.sku?.product)?.sku?.product),
      soldOut: Boolean(sku.soldOut ?? product.soldOut),
    });
  }
  return out.sort(
    (a, b) =>
      Number(a.soldOut) - Number(b.soldOut) || b.savings.percent - a.savings.percent,
  );
}

/** True when at least one Paket Hemat can be bought right now. */
export function hasBuyablePaket(list: PaketHemat[]): boolean {
  return list.some((p) => !p.soldOut);
}

/**
 * The headline "hemat hingga" figure: the biggest saving in rupiah still in
 * stock. Rupiah, not a percentage, as the client asked: "Hemat 4%" on a desk
 * undersells a saving of Rp 150.000.
 */
export function bestSaving(list: PaketHemat[]): number {
  return list.reduce((n, p) => (p.soldOut ? n : Math.max(n, p.savings.amount)), 0);
}

/** A borrowed item's picture: its primary, never an Additional photo (which may be a video). */
function borrowed(product: Pick<ApiProduct, "images"> | undefined): string | undefined {
  return product ? primaryImage(product)?.publicUrl : undefined;
}
